import i18n from "@/i18n";
import { LAYOUT } from "./ExcelLayout";
import { getRiskGroupByScore } from "@/util/RiskGroupUtil";

const list = {
  // =================================================================================================================
  components() {
    return [
      {
        component: "search-start-end",
        label: "작성일",
        value: "date",
        query: { ss: "startDate", ee: "endDate" },
        options: {
          default: { sd: "", ed: "" },
        },
      },
      { component: "search-disaster-types", label: "재해형태", value: "disaster", query: { dty: "disaster" }, options: {} },
      {
        component: "search-column-keyword",
        label: "검색조건",
        value: "column",
        query: { ck_t: "head", ck_k: "keyword" },
        options: { hiddenList: ["재해형태", "작성일"] },
      },
    ];
  },
  getConditions: (nav) => {
    const conditions = [];
    const search = nav.value;

    //  검색일
    if (search.date) {
      conditions.push({ key: "created_at", operator: ">=", value: search.date.startDate });
      conditions.push({ key: "created_at", operator: "<", value: search.date.endDate ? new Date(search.date.endDate).addDays(1).format("yyyy-MM-dd") : "" });
    }

    //  재해형태
    if (search.disaster) {
      conditions.push({ key: "body.riskList[%].input4", operator: "LIKE", value: `%${search.disaster}%` });
    }

    //  검색조건
    const heads = list.getHead().map((e) => {
      return { [e.name]: e.key };
    });
    // 배열의 객체들을 하나의 객체로 합치기
    const types = Object.assign({}, ...heads);
    if (search.column && search.column.keyword) {
      conditions.push({ key: types[search.column.head], operator: "LIKE", value: `%${search.column.keyword}%` });
    }
    return conditions;
  },

  // =================================================================================================================
  getHead: () => {
    return [
      {
        name: "회사명",
        key: "body.stdInfo.field_name",
      },
      {
        name: "공정명",
        key: "body.stdInfo.공정명",
      },
      {
        name: "재해형태",
        key: "body.riskList[%].input4",
      },
      {
        name: "작성자",
        key: "body.stdInfo.writer_name",
      },
      {
        name: "작성일",
        key: "created_at",
      },
    ];
  },
  getCol: (index, value, func) => {
    try {
      switch (index) {
      case 0:
        return func.getContent(value).stdInfo.field_name; // 회사명
      case 1:
        return func.getContent(value).stdInfo.공정명; // 공정명
      case 2: {
        const riskList = func.getContent(value).riskList;
        if (riskList.length > 0) {
          // 각 항목에서 '직접입력_' 접두어 제거 처리
          const processedItems = riskList
            .map((e) => {
              const item = e.input4 || "";
              // '직접입력_'으로 시작하면 접두어 제거하고 반환
              if (item.startsWith("직접입력_")) {
                return item.substring("직접입력_".length);
              }
              return i18n.t(item);
            })
            .filter(Boolean); // 빈 문자열 제거

          // 중복 제거 (Set 활용)
          const uniqueItems = [...new Set(processedItems)];

          return uniqueItems.join(", ");
        }
        return "";
      }
      case 3:
        return func.getContent(value).stdInfo.writer_name; // 작성자
      case 4:
        return func.getCreatedDate(value.created_at); // 작성일
      }
    } catch (e) {
      /*ignore*/
    }
    return "";
  },
  getSearchKeyValue: () => {},
  getSearchKeyword: () => {},

  // =================================================================================================================
  // 엑셀 내보내기 — 서식: public/fms/위험성평가_3등급_MIRAS.xlsx (scripts/generate-risk-3grade-template.js 로 생성)
  excelInfo: () => {
    // 체크박스형 이행확인 값을 엑셀 표기로 변환
    function checkMark(value) {
      return value ? "O" : "";
    }

    // '직접입력_' 접두어를 제거한 재해형태
    function disasterName(value) {
      return `${value || ""}`.replace("직접입력_", "").replace("직접 입력_", "");
    }

    return {
      filePath: "/fms/위험성평가_3등급_MIRAS.xlsx",
      downloadFileName: "위험성평가_3등급",
      worksheetName: (data) => {
        const body = data.body;
        return body && body.stdInfo && body.stdInfo.field_name ? body.stdInfo.field_name : "기타";
      },
      applyDataToSheet: async (worksheet, data, func) => {
        const body = data.body;
        const stdInfo = body.stdInfo || {};

        // ── 기본정보 ────────────────────────────────────────────
        const info = LAYOUT.info;
        worksheet.getRow(info.회사명.row).getCell(info.회사명.valueCol).value = stdInfo.field_name;
        worksheet.getRow(info.공정명.row).getCell(info.공정명.valueCol).value = stdInfo.공정명;
        worksheet.getRow(info.작성자.row).getCell(info.작성자.valueCol).value = stdInfo.writer_name;
        worksheet.getRow(info.작업내용.row).getCell(info.작업내용.valueCol).value = stdInfo.작업내용;
        worksheet.getRow(info.작성일.row).getCell(info.작성일.valueCol).value = stdInfo.date;

        // ── ❶ 위험성평가표 ──────────────────────────────────────
        const riskList = body.riskList || [];
        const riskRows = riskList.map((risk, index) => {
          const score = Number(risk.가능성) * Number(risk.중대성) || "";
          return [
            index + 1, // No.
            risk.input1, // ①공종분류
            risk.input2, // ②단위작업
            risk.input3, // ③위험요인
            disasterName(risk.input4), // 재해형태
            risk.가능성 || "", // 가능성
            risk.중대성 || "", // 중대성
            score, // 위험점수
            risk.input5 || getRiskGroupByScore(score), // ④위험도 등급(상/중/하)
            risk.input6, // ⑤위험방지대책
            risk.input7, // ⑥개선조치 담당자
            risk.input8, // 조치예정일
            checkMark(risk.input9), // ➉이행확인 근로자대표
            checkMark(risk.input10), // ➉이행확인 관리감독자
          ];
        });

        worksheet.spliceRows(LAYOUT.risk.dataStartRow, LAYOUT.risk.templateRows, ...riskRows);
        for (let i = 0; i < riskRows.length; i++) {
          worksheet.getRow(LAYOUT.risk.dataStartRow + i).height = 40;
        }
        const riskShift = riskRows.length - LAYOUT.risk.templateRows;

        // ── ❷ ➆ 위험성 결정, 감소대책 수립 및 실행계획 확인 ──────
        const managerList = body.managerList || [];
        const managerRows = managerList.map((manager) => [manager.target, manager.position, manager.type, manager.name]);

        const managerStartRow = LAYOUT.manager.dataStartRow + riskShift;
        worksheet.spliceRows(managerStartRow, LAYOUT.manager.templateRows, ...managerRows);
        for (let i = 0; i < managerRows.length; i++) {
          worksheet.getRow(managerStartRow + i).height = 26;
        }
        const managerShift = managerRows.length - LAYOUT.manager.templateRows;

        // ── ❸ 개선현황 (조치 전/후 사진) ────────────────────────
        const photoList = body.photoListBeforeAfter || [];
        const photoStartRow = LAYOUT.photo.dataStartRow + riskShift + managerShift;
        const splitCol = LAYOUT.photo.beforeAfterSplitCol;

        worksheet.spliceRows(photoStartRow, LAYOUT.photo.templateRows, ...photoList.map((photo, index) => [index + 1]));

        for (let i = 0; i < photoList.length; i++) {
          const rowIndex = photoStartRow + i;
          worksheet.getRow(rowIndex).height = 200;

          // tl.col / tl.row 는 0-based, addImage 는 비동기이므로 순차 처리한다
          const anchors = [
            { image: photoList[i].이전사진, col: 1 },
            { image: photoList[i].이후사진, col: splitCol - 1 },
          ];

          for (const anchor of anchors) {
            const image = anchor.image;
            if (!image || !image.id || !image.path) continue;

            const paths = image.path.split("/");
            const hashedName = paths[paths.length - 1];
            await func.addImage(hashedName, {
              tl: { col: anchor.col + 0.5, row: rowIndex - 1 + 0.01 },
              ext: { width: 460, height: 250 },
            });
          }
        }
      },
    };
  },
};
export default list;
